import { useEffect, useState } from 'react';
import axios from 'axios';

export default function BatchEmailDialog({ open, leadIds, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [testMode, setTestMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setResult(null);
    setSending(false);
    setTestMode(true);
    setLoading(true);
    axios.get('/api/email-templates')
      .then((r) => {
        const list = r.data?.templates || [];
        setTemplates(list);
        if (list.length > 0) setSelectedId(list[0].id);
      })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  async function handleSend() {
    setSending(true);
    setError(null);
    try {
      const r = await axios.post('/api/leads/batch-send-email', {
        lead_ids: leadIds,
        template_id: selectedId,
        test_mode: testMode
      });
      setResult(r.data);
      if (!testMode && onSent) onSent(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSending(false);
    }
  }

  const tpl = templates.find((t) => t.id === selectedId);

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}>
      <div className="modal-card modal-wide" role="dialog" aria-modal="true">
        <header className="modal-head">
          <h3>{result ? (testMode ? 'Test Preview Sent' : 'Batch Sent') : `Email ${leadIds.length} Leads`}</h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={sending} aria-label="Close">×</button>
        </header>

        {/* RESULT VIEW */}
        {result && (
          <div className="modal-body">
            {testMode ? (
              <>
                <p>✓ Preview sent to <code>david@xsitegroup.ca</code>.</p>
                <p className="modal-sub">
                  Rendered for <strong>{result.previewed_for_company}</strong>. If it looks right, uncheck
                  Test Mode and re-send to fire to all <strong>{result.total_would_send}</strong> recipients.
                </p>
              </>
            ) : (
              <>
                <p>✓ <strong>{result.sent}</strong> of <strong>{result.total}</strong> emails sent.</p>
                {result.skipped_missing_email > 0 && (
                  <p className="modal-sub">⚠ {result.skipped_missing_email} skipped (no email address on record).</p>
                )}
                {result.failed && result.failed.length > 0 && (
                  <>
                    <p className="modal-sub">⚠ {result.failed.length} failed:</p>
                    <ul style={{ fontSize: 'var(--text-small)', color: 'var(--color-bad)' }}>
                      {result.failed.map((f) => (
                        <li key={f.lead_id}>{f.company || `Lead #${f.lead_id}`}: {f.error}</li>
                      ))}
                    </ul>
                  </>
                )}
                <p className="modal-sub">
                  Open tracking is now live for these sends. Check Email Engagement on the Dashboard or the
                  Email Campaigns page in the sidebar to watch opens roll in.
                </p>
              </>
            )}
            <div className="form-actions" style={{ marginTop: 'var(--space-3)' }}>
              {testMode && !result.failed && (
                <button type="button" className="btn-primary" onClick={() => { setResult(null); setTestMode(false); }}>
                  Looks good — Send LIVE to all {result.total_would_send}
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        )}

        {/* COMPOSE VIEW */}
        {!result && (
          <div className="modal-body">
            {loading && <p className="modal-sub">Loading templates…</p>}
            {error && <p className="error">{error}</p>}
            {!loading && templates.length > 0 && (
              <>
                <div className="form-grid">
                  <label className="form-field form-field-wide">
                    <span>Template</span>
                    <select value={selectedId || ''} onChange={(e) => setSelectedId(Number(e.target.value))}>
                      {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </label>
                  {tpl && (
                    <>
                      <label className="form-field form-field-wide">
                        <span>Subject (auto-rendered per recipient)</span>
                        <input type="text" value={tpl.subject} readOnly className="cell-mono" />
                      </label>
                      <label className="form-field form-field-wide">
                        <span>Body preview</span>
                        <textarea
                          rows={6}
                          value={tpl.body.length > 800 ? tpl.body.slice(0, 800) + '\n\n...(truncated for preview)' : tpl.body}
                          readOnly
                          className="send-email-body"
                        />
                      </label>
                    </>
                  )}
                </div>

                <div className={`test-mode-panel test-mode-${testMode ? 'on' : 'off'}`}>
                  <label className="test-mode-toggle">
                    <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} disabled={sending} />
                    <span className="test-mode-label">Test Mode</span>
                  </label>
                  {testMode ? (
                    <p className="test-mode-status">
                      ONE preview will be sent to <strong>david@xsitegroup.ca</strong> using the first selected
                      lead's merge values. None of the other {leadIds.length - 1} leads will receive anything.
                    </p>
                  ) : (
                    <p className="test-mode-status">
                      <strong>LIVE</strong> — will send to <strong>all {leadIds.length} selected leads</strong>.
                      A note will be logged on each lead's record. This may take {Math.ceil(leadIds.length * 0.3)}s.
                    </p>
                  )}
                </div>

                <div className="form-actions" style={{ marginTop: 'var(--space-3)' }}>
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
                  <button type="button" className="btn-primary" onClick={handleSend} disabled={sending || !selectedId}>
                    {sending
                      ? (testMode ? 'Sending preview…' : `Sending… (this can take ${Math.ceil(leadIds.length * 0.3)}s)`)
                      : (testMode ? 'Send Test Preview' : `Send LIVE to ${leadIds.length}`)}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
